import type { FormEvent } from 'react'
import { ArrowRight, ChevronUp, ChevronDown } from 'lucide-react'
import { motion } from 'framer-motion'
import type { FormData, FormErrors } from './types'
import { CURRENCIES, PAYMENT_METHODS } from './types'
import PaymentCard from './PaymentCard'
import AnimatedTooltip from './AnimatedTooltip'
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
} from '@mui/material'
import Grid from '@mui/material/Grid'

interface DonationFormProps {
  formData: FormData
  errors: FormErrors
  isSubmitting: boolean
  onInputChange: (field: keyof FormData, value: string) => void
  onSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>
}

export default function DonationForm({
  formData,
  errors,
  isSubmitting,
  onInputChange,
  onSubmit,
}: DonationFormProps) {
  const selectedCurrency =
    CURRENCIES.find((c) => c.code === formData.currency) || CURRENCIES[0]

  const selectedPaymentMethod =
    PAYMENT_METHODS.find((p) => p.id === formData.paymentMethod) ||
    PAYMENT_METHODS[0]

  const handlePaymentMethodSelect = (item: { id: string; name: string }) => {
    onInputChange('paymentMethod', item.id)
  }

  const handleAmountIncrement = () => {
    const currentValue = parseFloat(formData.amount) || 0
    const newValue = (currentValue + 0.01).toFixed(2)
    onInputChange('amount', newValue)
  }

  const handleAmountDecrement = () => {
    const currentValue = parseFloat(formData.amount) || 0
    if (currentValue > 0) {
      const newValue = Math.max(0, currentValue - 0.01).toFixed(2)
      onInputChange('amount', newValue)
    }
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
      <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, boxShadow: 4, p: { xs: 2, sm: 3 } }}>
        <Box component="form" onSubmit={onSubmit}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
              Payment Method
            </Typography>
            <Grid container spacing={3} alignItems="flex-start">
              <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex', justifyContent: { xs: 'center', lg: 'flex-start' } }}>
                <PaymentCard
                  paymentMethod={selectedPaymentMethod}
                  cardHolder={formData.email ? formData.email.split('@')[0].toUpperCase() : 'CARD HOLDER'}
                />
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', lg: 'flex-start' }, justifyContent: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: { xs: 'center', lg: 'left' } }}>
                  Select your preferred payment method
                </Typography>
                <AnimatedTooltip
                  items={PAYMENT_METHODS.map((method) => ({
                    id: method.id,
                    name: method.name,
                    color: method.color,
                    image: method.image,
                  }))}
                  onSelect={handlePaymentMethodSelect}
                  selectedId={formData.paymentMethod}
                />
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel id="currency-label">Currency</InputLabel>
                <Select
                  labelId="currency-label"
                  id="currency"
                  value={formData.currency}
                  label="Currency"
                  onChange={(e) => onInputChange('currency', e.target.value)}
                >
                  {CURRENCIES.map((currency) => (
                    <MenuItem key={currency.code} value={currency.code}>
                      {currency.code} ({currency.name})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                id="amount"
                label="Donation Amount"
                fullWidth
                value={formData.amount}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, '')
                  onInputChange('amount', value)
                }}
                placeholder="e.g. 10.00"
                error={!!errors.amount}
                helperText={errors.amount}
                InputProps={{
                  startAdornment: <InputAdornment position="start">{selectedCurrency.symbol}</InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box sx={{ display: 'flex', flexDirection: 'column', mr: -1 }}>
                        <IconButton size="small" onClick={handleAmountIncrement}>
                          <ChevronUp size={14} />
                        </IconButton>
                        <IconButton size="small" onClick={handleAmountDecrement}>
                          <ChevronDown size={14} />
                        </IconButton>
                      </Box>
                    </InputAdornment>
                  ),
                }}
                inputProps={{ inputMode: 'decimal', 'aria-label': 'Enter donation amount' }}
                aria-invalid={!!errors.amount}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <TextField
              id="email"
              label="Email"
              fullWidth
              value={formData.email}
              onChange={(e) => onInputChange('email', e.target.value)}
              placeholder="your@email.com"
              error={!!errors.email}
              helperText={errors.email || 'Optional - for receipt'}
              aria-invalid={!!errors.email}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  borderRadius: '999px',
                  px: 3,
                  py: 1.5,
                  gap: 1,
                  backgroundColor: '#78350f',
                  '&:hover': { backgroundColor: '#7c2d12' },
                }}
              >
                {isSubmitting ? (
                  <>
                    <CircularProgress size={20} sx={{ color: '#fff', mr: 1 }} />
                    Processing...
                  </>
                ) : (
                  <>
                    Proceed to Paynow
                    <ArrowRight size={18} />
                  </>
                )}
              </Button>
            </motion.div>
          </Box>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Divider />
          <Box sx={{ pt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Secure payments powered by Paynow Zimbabwe
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              All transactions are encrypted and processed securely.
            </Typography>
          </Box>
        </Box>
      </Box>
    </motion.div>
  )
}

